import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import { productEventsQueryKey, type ProductEventLink } from './useProductEventsQuery';

export type ProductEventEntryMode = ProductEventLink['entryMode'];

export interface ProductEventLinkInput {
  eventId: string;
  entryMode: ProductEventEntryMode;
  minOrderAmount?: number | null;
  minQuantity?: number | null;
}

export interface SyncProductEventsInput {
  productId: string;
  links: ReadonlyArray<ProductEventLinkInput>;
}

const buildBody = (links: ReadonlyArray<ProductEventLinkInput>) => ({
  links: links.map((link) => ({
    eventId: link.eventId,
    entryMode: link.entryMode,
    ...(link.minOrderAmount != null ? { minOrderAmount: link.minOrderAmount } : {}),
    ...(link.minQuantity != null ? { minQuantity: link.minQuantity } : {}),
  })),
});

/**
 * Replace the product's event links (PUT /admin/v1/products/{id}/events).
 * Empty links unlinks all. Sends an Idempotency-Key matching the write filter.
 */
export const useSyncProductEventsMutation = () => {
  const qc = useQueryClient();
  return useMutation<ProductEventLink[], Error, SyncProductEventsInput>({
    mutationFn: async ({ productId, links }) => {
      const path = `/admin/v1/products/${productId}/events`;
      const body = buildBody(links);
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`product-events sync failed: ${res.status}`);
      const env = (await res.json()) as { data?: ProductEventLink[] };
      return Array.isArray(env.data) ? env.data : [];
    },
    onSuccess: (_data, { productId }) => {
      void qc.invalidateQueries({ queryKey: productEventsQueryKey(productId) });
    },
  });
};
